/* eslint-disable no-nested-ternary */
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Icon,
	List,
	ListItem,
	ListItemText
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDBadge from "components/Basics/MDBadge";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import FaiqActions from "redux-react/actions/faiqActions";
import { socket } from "redux-react/middleware/ws";

/**
 * Small stat tile (assets, sources, knowledges, expectedVectors)
 */
const TotalTile = ({ label, value }) => (
	<Grid item xs={6} sm={4} md={3} lg>
		<MDBox
			px={1.5}
			py={1}
			borderRadius="md"
			sx={{
				bgcolor: "grey.100",
				border: "1px solid",
				borderColor: "grey.300",
				height: "100%"
			}}
		>
			<MDTypography
				variant="caption"
				color="text"
				fontWeight="medium"
				sx={{ textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5, lineHeight: 1.2 }}
				display="block"
			>
				{label}
			</MDTypography>
			<MDTypography variant="h5" color="dark" fontWeight="bold" lineHeight={1.1}>
				{value ?? "-"}
			</MDTypography>
		</MDBox>
	</Grid>
);

/**
 * Compact section header: title + count badge, used across all anomaly sections.
 */
const SectionHeader = ({ title, count, isEmpty }) => (
	<MDBox display="flex" alignItems="center" gap={1}>
		<MDTypography
			variant="button"
			fontWeight="bold"
			sx={{ textTransform: "uppercase", fontSize: 11 }}
		>
			{title}
		</MDTypography>
		<MDBadge
			size="xs"
			container
			variant="contained"
			color={isEmpty ? "success" : "error"}
			badgeContent={count}
		/>
	</MDBox>
);

/**
 * One expandable anomaly row. Lazy-loads its Mongo detail (name + metadatas) on
 * first expand. `detailsType` may be null when no drill-down is available
 * (e.g. orphan vectors that exist only in the vector DB).
 */
const AnomalyItem = ({ assistantID, label, id, mapping, detailsType: detailsTypeProps }) => {
	const dispatch = useDispatch();
	const [expanded, setExpanded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [detail, setDetail] = useState(null);
	const [loaded, setLoaded] = useState(false);

	const detailsType = mapping?.[id] ?? detailsTypeProps;

	const handleChange = (_, isExpanded) => {
		setExpanded(isExpanded);
		if (isExpanded && !loaded && detailsType && id) {
			setLoading(true);
			dispatch(
				FaiqActions.dataCheckDetails(
					assistantID,
					{ type: detailsType, ids: [id] },
					res => {
						const items = res?.data?.items ?? [];
						setDetail(items[0] ?? null);
						setLoading(false);
						setLoaded(true);
					},
					() => {
						setLoading(false);
						setLoaded(true);
					}
				)
			);
		}
	};

	return (
		<Accordion
			expanded={expanded}
			onChange={handleChange}
			disableGutters
			sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}
		>
			<AccordionSummary
				expandIcon={detailsType ? <ExpandMoreIcon /> : null}
				sx={{ minHeight: 32, "& .MuiAccordionSummary-content": { my: 0.5 } }}
			>
				<MDTypography variant="body2">{label}</MDTypography>
			</AccordionSummary>
			{detailsType && (
				<AccordionDetails sx={{ bgcolor: "grey.200", borderRadius: 1 }}>
					{loading ? (
						<MDBox display="flex" alignItems="center">
							<CircularProgress size={14} sx={{ mr: 1 }} />
							<MDTypography variant="caption">{t("CONFIG.dataCheck.loadingDetails")}</MDTypography>
						</MDBox>
					) : detail ? (
						<MDBox
							component="pre"
							sx={{
								m: 0,
								fontSize: 11,
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
								maxHeight: 240,
								overflow: "auto"
							}}
						>
							{JSON.stringify(detail, null, 2)}
						</MDBox>
					) : (
						<MDTypography variant="caption">{t("CONFIG.dataCheck.notFound")}</MDTypography>
					)}
				</AccordionDetails>
			)}
		</Accordion>
	);
};

/**
 * A section listing one type of anomaly. Shows a green "no anomaly" state when empty.
 *
 * @param {Array} items raw items (string uuid OR object with code/uuid)
 * @param {(item) => string} renderItem label renderer
 * @param {(item) => string} getId extracts the lookup id for drill-down (returns null to disable)
 */
const AnomalySection = ({ assistantID, title, items, renderItem, getId, mapping, detailsType }) => {
	const isEmpty = !items || items.length === 0;
	return (
		<MDBox mb={1.5}>
			<MDBox mb={0.75}>
				<SectionHeader title={title} count={isEmpty ? "0" : items.length} isEmpty={isEmpty} />
			</MDBox>
			{isEmpty ? (
				<MDBox display="flex" flexDirection="row" alignItems="center">
					<Icon color="success" fontSize="small" sx={{ mr: 0.75, fontSize: 16 }}>
						check_circle
					</Icon>
					<MDTypography variant="caption" color="text">
						{t("CONFIG.dataCheck.noAnomaly")}
					</MDTypography>
				</MDBox>
			) : (
				<List
					dense
					disablePadding
					sx={{
						maxHeight: 220,
						overflow: "auto",
						bgcolor: "grey.100",
						borderRadius: 1,
						border: "1px solid",
						borderColor: "grey.300"
					}}
				>
					{items.map((item, index) => {
						const id = getId ? getId(item) : null;
						return (
							<ListItem key={index} divider sx={{ display: "block", py: 0 }}>
								{id ? (
									<AnomalyItem
										assistantID={assistantID}
										label={renderItem(item)}
										id={id}
										detailsType={detailsType}
										mapping={mapping}
									/>
								) : (
									<ListItemText
										primaryTypographyProps={{ variant: "body2" }}
										primary={renderItem(item)}
									/>
								)}
							</ListItem>
						);
					})}
				</List>
			)}
		</MDBox>
	);
};

/**
 * Confirmation dialog for the patch operation. Lists what is about to happen
 * based on the current Mongo + vector reports.
 */
const PatchConfirmDialog = ({ open, onClose, onConfirm, counts }) => {
	const nothing =
		counts.sources === 0 &&
		counts.knowledges === 0 &&
		counts.duplicateSources === 0 &&
		counts.duplicateKnowledges === 0 &&
		counts.vectorize === 0 &&
		counts.delete === 0;
	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{t("CONFIG.dataCheck.patchConfirmTitle")}</DialogTitle>
			<DialogContent>
				{nothing ? (
					<MDTypography variant="body2">{t("CONFIG.dataCheck.patchConfirmNothing")}</MDTypography>
				) : (
					<>
						<MDTypography variant="body2" mb={1}>
							{t("CONFIG.dataCheck.patchConfirmBody")}
						</MDTypography>
						<MDBox component="ul" sx={{ pl: 3, m: 0 }}>
							{counts.sources > 0 && (
								<li>
									<MDTypography variant="body2">
										{t("CONFIG.dataCheck.patchConfirmDeleteSources", { count: counts.sources })}
									</MDTypography>
								</li>
							)}
							{counts.knowledges > 0 && (
								<li>
									<MDTypography variant="body2">
										{t("CONFIG.dataCheck.patchConfirmDeleteKnowledges", {
											count: counts.knowledges
										})}
									</MDTypography>
								</li>
							)}
							{counts.duplicateSources > 0 && (
								<li>
									<MDTypography variant="body2">
										{t("CONFIG.dataCheck.patchConfirmDeleteDuplicateSources", {
											count: counts.duplicateSources
										})}
									</MDTypography>
								</li>
							)}
							{counts.duplicateKnowledges > 0 && (
								<li>
									<MDTypography variant="body2">
										{t("CONFIG.dataCheck.patchConfirmDeleteDuplicateKnowledges", {
											count: counts.duplicateKnowledges
										})}
									</MDTypography>
								</li>
							)}
							{counts.vectorize > 0 && (
								<li>
									<MDTypography variant="body2">
										{t("CONFIG.dataCheck.patchConfirmVectorize", { count: counts.vectorize })}
									</MDTypography>
								</li>
							)}
							{counts.delete > 0 && (
								<li>
									<MDTypography variant="body2">
										{t("CONFIG.dataCheck.patchConfirmDeleteVectors", { count: counts.delete })}
									</MDTypography>
								</li>
							)}
						</MDBox>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<MDButton variant="text" color="secondary" onClick={onClose}>
					{t("CONFIG.dataCheck.cancel")}
				</MDButton>
				<MDButton variant="gradient" color="warning" onClick={onConfirm} disabled={nothing}>
					{t("CONFIG.dataCheck.patchConfirmAction")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

/**
 * Data integrity check dialog.
 * - Immediately calls GET /faiq/dataCheck/:assistantID and renders the Mongo report (HTTP 202).
 * - Listens to the `faiq_data_check_result` websocket event for the asynchronous vector (Pinecone) result.
 * - Supports per-item drill-down (sources / knowledges) and a Patch action to fix all anomalies at once.
 */
const DataCheckDialog = ({ open, onClose, assistantID }) => {
	const dispatch = useDispatch();

	const [loadingMongo, setLoadingMongo] = useState(false);
	const [mongoError, setMongoError] = useState(false);
	const [mongoReport, setMongoReport] = useState(null);
	const [vectorReport, setVectorReport] = useState(null);

	// Patch state
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [patching, setPatching] = useState(false);
	const [patchVectorResult, setPatchVectorResult] = useState(null);
	const [patchMongoResult, setPatchMongoResult] = useState(null);

	const startCheck = () => {
		setLoadingMongo(true);
		setMongoError(false);
		setMongoReport(null);
		setVectorReport(null);
		setPatchVectorResult(null);
		setPatchMongoResult(null);

		dispatch(
			FaiqActions.dataCheck(
				assistantID,
				res => {
					setLoadingMongo(false);
					setMongoReport(res?.data ?? null);
				},
				() => {
					setLoadingMongo(false);
					setMongoError(true);
				}
			)
		);
	};

	const onDataCheckResult = data => {
		// Guard: only handle results for the assistant we are checking
		if (data?.assistantID && assistantID && data.assistantID !== assistantID) {
			return;
		}

		if (data?.stage === "vector") {
			setVectorReport(data);
		} else if (data?.stage === "mongo") {
			// Mirror of the 202 report - only used as a fallback if the HTTP response was missed
			setMongoReport(prev => prev ?? data);
		}
	};

	const onPatchResult = data => {
		if (data?.assistantID && assistantID && data.assistantID !== assistantID) {
			return;
		}
		setPatchVectorResult(data);
		setPatching(false);
		// Refresh the check so the user sees the updated state
		startCheck();
	};

	useEffect(() => {
		socket.on("faiq_data_check_result", onDataCheckResult);
		socket.on("faiq_data_check_patch_result", onPatchResult);
		return () => {
			socket.off("faiq_data_check_result", onDataCheckResult);
			socket.off("faiq_data_check_patch_result", onPatchResult);
		};
	}, [assistantID]);

	useEffect(() => {
		if (open) {
			startCheck();
		}
	}, [open]);

	const handleClose = () => {
		setMongoReport(null);
		setVectorReport(null);
		setMongoError(false);
		setLoadingMongo(false);
		setPatching(false);
		setPatchVectorResult(null);
		setPatchMongoResult(null);
		onClose();
	};

	const totals = mongoReport?.totals ?? {};

	const patchCounts = {
		sources: mongoReport?.sourcesWithoutAsset?.length ?? 0,
		knowledges: mongoReport?.knowledgeWithoutSource?.length ?? 0,
		duplicateSources: mongoReport?.totals?.duplicateSources ?? 0,
		duplicateKnowledges: mongoReport?.totals?.duplicateKnowledges ?? 0,
		vectorize: (vectorReport?.missingInVectorDB ?? []).filter(
			uuid => !(mongoReport?.knowledgeWithoutSource ?? []).some(k => k?.uuid === uuid)
		).length,
		delete: vectorReport?.orphansInVectorDB?.length ?? 0
	};

	const hasAnomaly =
		patchCounts.sources > 0 ||
		patchCounts.knowledges > 0 ||
		patchCounts.duplicateSources > 0 ||
		patchCounts.duplicateKnowledges > 0 ||
		patchCounts.vectorize > 0 ||
		patchCounts.delete > 0;

	const launchPatch = () => {
		setConfirmOpen(false);
		setPatching(true);
		setPatchVectorResult(null);
		setPatchMongoResult(null);

		dispatch(
			FaiqActions.dataCheckPatch(
				assistantID,
				{
					sourcesWithoutAsset: mongoReport?.sourcesWithoutAsset ?? [],
					knowledgeWithoutSource: mongoReport?.knowledgeWithoutSource ?? [],
					duplicateSources: mongoReport?.duplicateSources ?? [],
					duplicateKnowledges: mongoReport?.duplicateKnowledges ?? [],
					missingInVectorDB: vectorReport?.missingInVectorDB ?? [],
					orphansInVectorDB: vectorReport?.orphansInVectorDB ?? [],
					mapping: mongoReport?.mapping ?? {}
				},
				res => {
					setPatchMongoResult(res?.data ?? null);
					// Keep `patching` true until WS vector result comes back
				},
				() => {
					setPatching(false);
				}
			)
		);
	};

	const renderVectorPart = () => {
		// vector model not found on the vector component side
		if (vectorReport && vectorReport.success === false) {
			return (
				<MDBox bgColor="error" p={2} borderRadius="lg" textAlign="center">
					<MDTypography variant="button" color="white">
						{vectorReport.error === "vectorModelNotFound"
							? t("CONFIG.dataCheck.vectorModelNotFound")
							: t("CONFIG.dataCheck.error")}
					</MDTypography>
				</MDBox>
			);
		}

		// vector result not received yet
		if (!vectorReport) {
			return (
				<MDBox display="flex" flexDirection="row" alignItems="center">
					<CircularProgress size={18} color="info" sx={{ mr: 2 }} />
					<MDTypography variant="body2" color="text">
						{t("CONFIG.dataCheck.vectorPending")}
					</MDTypography>
				</MDBox>
			);
		}

		return (
			<>
				<MDBox display="flex" flexDirection="row" gap={1} mb={1.5}>
					<MDBox
						px={1.5}
						py={0.5}
						borderRadius="md"
						sx={{ bgcolor: "grey.100", border: "1px solid", borderColor: "grey.300" }}
					>
						<MDTypography variant="caption" color="text">
							{t("CONFIG.dataCheck.totalMongo")}:{" "}
							<MDTypography variant="caption" fontWeight="bold" color="dark" component="span">
								{vectorReport.totalMongo ?? "-"}
							</MDTypography>
						</MDTypography>
					</MDBox>
					<MDBox
						px={1.5}
						py={0.5}
						borderRadius="md"
						sx={{ bgcolor: "grey.100", border: "1px solid", borderColor: "grey.300" }}
					>
						<MDTypography variant="caption" color="text">
							{t("CONFIG.dataCheck.totalVectorDB")}:{" "}
							<MDTypography variant="caption" fontWeight="bold" color="dark" component="span">
								{vectorReport.totalVectorDB ?? "-"}
							</MDTypography>
						</MDTypography>
					</MDBox>

					<MDBox
						px={1.5}
						py={0.5}
						borderRadius="md"
						sx={{ bgcolor: "grey.100", border: "1px solid", borderColor: "grey.300" }}
					>
						<MDTypography variant="caption" color="text">
							Vecteurs omis (correspond aux thèmes non indexés):{" "}
							<MDTypography variant="caption" fontWeight="bold" color="dark" component="span">
								{vectorReport.omitedVectors ?? "0"}
							</MDTypography>
						</MDTypography>
					</MDBox>
				</MDBox>
				<AnomalySection
					assistantID={assistantID}
					title={t("CONFIG.dataCheck.missingInVectorDB")}
					items={vectorReport.missingInVectorDB}
					renderItem={uuid => uuid}
					getId={uuid => uuid}
					mapping={mongoReport?.mapping ?? {}}
				/>
				<AnomalySection
					assistantID={assistantID}
					title={t("CONFIG.dataCheck.orphansInVectorDB")}
					items={vectorReport.orphansInVectorDB}
					renderItem={uuid => uuid}
					getId={uuid => uuid}
					detailsType="vector"
				/>
			</>
		);
	};

	const renderPatchStatus = () => {
		if (!patching && !patchMongoResult && !patchVectorResult) return null;
		return (
			<MDBox
				mt={2}
				p={2}
				borderRadius="lg"
				sx={{ bgcolor: patchVectorResult?.success === false ? "error.light" : "info.light" }}
			>
				{patching && !patchVectorResult && (
					<MDBox display="flex" alignItems="center" mb={1}>
						<CircularProgress size={16} sx={{ mr: 1 }} />
						<MDTypography variant="body2">{t("CONFIG.dataCheck.patchPending")}</MDTypography>
					</MDBox>
				)}
				{patchMongoResult && (
					<MDTypography variant="caption" display="block">
						{t("CONFIG.dataCheck.patchMongoDone", {
							sourcesDeleted: patchMongoResult.sourcesDeleted ?? 0,
							knowledgesDeleted: patchMongoResult.knowledgesDeleted ?? 0,
							duplicateSourcesDeleted: patchMongoResult.duplicateSourcesDeleted ?? 0,
							duplicateKnowledgesDeleted: patchMongoResult.duplicateKnowledgesDeleted ?? 0
						})}
					</MDTypography>
				)}
				{patchVectorResult && (
					<MDTypography variant="caption" display="block">
						{patchVectorResult.success === false
							? t("CONFIG.dataCheck.patchError")
							: t("CONFIG.dataCheck.patchVectorDone", {
									vectorized: patchVectorResult.vectorized ?? 0,
									vectorsDeleted: patchVectorResult.vectorsDeleted ?? 0,
									failures: patchVectorResult.vectorizeFailures ?? 0
								})}
					</MDTypography>
				)}
			</MDBox>
		);
	};

	const renderContent = () => {
		if (loadingMongo) {
			return (
				<MDBox
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					p={4}
				>
					<CircularProgress color="info" />
					<MDTypography variant="body2" color="text" mt={2}>
						{t("CONFIG.dataCheck.running")}
					</MDTypography>
				</MDBox>
			);
		}

		if (mongoError) {
			return (
				<MDBox bgColor="error" p={3} borderRadius="lg" textAlign="center">
					<MDTypography variant="h6" color="white">
						{t("CONFIG.dataCheck.error")}
					</MDTypography>
				</MDBox>
			);
		}

		if (!mongoReport) {
			return null;
		}

		return (
			<MDBox display="flex" flexDirection="column">
				{/* Totals */}
				<Grid container spacing={1} mt={1} mb={2}>
					<TotalTile label={t("CONFIG.dataCheck.assets")} value={totals.assets} />
					<TotalTile label={t("CONFIG.dataCheck.sources")} value={totals.sources} />
					<TotalTile label={t("CONFIG.dataCheck.summarySources")} value={totals.summarySources} />

					<TotalTile label={t("CONFIG.dataCheck.knowledges")} value={totals.knowledges} />
					<TotalTile
						label={t("CONFIG.dataCheck.knowledgesVectors")}
						value={totals.knowledgesVectors}
					/>
					<TotalTile
						label={t("CONFIG.dataCheck.testingAssociations")}
						value={totals.testingAssociations}
					/>
					<TotalTile label={t("CONFIG.dataCheck.expectedVectors")} value={totals.expectedVectors} />
					<TotalTile
						label={t("CONFIG.dataCheck.duplicateSources")}
						value={totals.duplicateSources ?? 0}
					/>
					<TotalTile
						label={t("CONFIG.dataCheck.duplicateKnowledges")}
						value={totals.duplicateKnowledges ?? 0}
					/>
				</Grid>

				{/* Mongo anomalies */}
				<MDBox mb={1.5} pb={0.5} sx={{ borderBottom: "2px solid", borderColor: "info.main" }}>
					<MDTypography variant="subtitle2" fontWeight="bold" color="dark">
						{t("CONFIG.dataCheck.mongoSection")}
					</MDTypography>
				</MDBox>
				<AnomalySection
					assistantID={assistantID}
					title={t("CONFIG.dataCheck.sourcesWithoutAsset")}
					items={mongoReport.sourcesWithoutAsset}
					renderItem={item => item?.code ?? JSON.stringify(item)}
					getId={item => item?.code}
					detailsType="source"
				/>
				<AnomalySection
					assistantID={assistantID}
					title={t("CONFIG.dataCheck.knowledgeWithoutSource")}
					items={mongoReport.knowledgeWithoutSource}
					renderItem={item => `${item?.uuid ?? ""} (${item?.source ?? ""})`}
					getId={item => item?.uuid}
					detailsType="knowledge"
				/>
				<AnomalySection
					assistantID={assistantID}
					title={t("CONFIG.dataCheck.duplicateSources")}
					items={mongoReport.duplicateSources}
					renderItem={item => `${item?.asset ?? ""} (×${item?.count ?? ""})`}
					getId={null}
				/>
				<AnomalySection
					assistantID={assistantID}
					title={t("CONFIG.dataCheck.duplicateKnowledges")}
					items={mongoReport.duplicateKnowledges}
					renderItem={item => item?.uuid ?? JSON.stringify(item)}
					getId={item => item?.uuid}
					detailsType="knowledge"
				/>

				{/* Vector anomalies */}
				<MDBox
					mt={2}
					mb={1.5}
					pb={0.5}
					sx={{ borderBottom: "2px solid", borderColor: "info.main" }}
				>
					<MDTypography variant="subtitle2" fontWeight="bold" color="dark">
						{t("CONFIG.dataCheck.vectorSection")}
					</MDTypography>
				</MDBox>
				<MDBox>{renderVectorPart()}</MDBox>

				{renderPatchStatus()}

				{/* Patch button */}
				<MDBox mt={3} display="flex" justifyContent="flex-end">
					<MDButton
						variant="gradient"
						color="warning"
						disabled={!hasAnomaly || patching || !vectorReport}
						onClick={() => setConfirmOpen(true)}
					>
						<Icon sx={{ mr: 1 }}>auto_fix_high</Icon>
						{t("CONFIG.dataCheck.patch")}
					</MDButton>
				</MDBox>
			</MDBox>
		);
	};

	return (
		<>
			<Dialog fullWidth maxWidth="xxl" open={open} onClose={handleClose}>
				<MDBox
					display="flex"
					flexDirection="row"
					justifyContent="space-between"
					alignItems="center"
					px={3}
					py={1.5}
					sx={{ bgcolor: "grey.100", borderBottom: "1px solid", borderColor: "grey.300" }}
				>
					<MDBox display="flex" alignItems="center" gap={1}>
						<Icon color="info">fact_check</Icon>
						<MDTypography variant="h6" fontWeight="bold">
							{t("CONFIG.dataCheck.title")}
						</MDTypography>
					</MDBox>
					<MDBox display="flex" flexDirection="row" gap={1}>
						<MDButton
							variant="outlined"
							color="info"
							size="small"
							disabled={loadingMongo || patching}
							onClick={startCheck}
						>
							<Icon sx={{ mr: 0.5 }}>refresh</Icon>
							{t("CONFIG.dataCheck.relaunch")}
						</MDButton>
						<MDButton variant="contained" color="info" size="small" onClick={handleClose}>
							{t("SETTINGS.close")}
						</MDButton>
					</MDBox>
				</MDBox>
				<DialogContent sx={{ pt: 2 }}>{renderContent()}</DialogContent>
			</Dialog>

			<PatchConfirmDialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				onConfirm={launchPatch}
				counts={patchCounts}
			/>
		</>
	);
};

export default DataCheckDialog;
